import { db } from '../db';
import { queueRepo } from '../db/queue';

/**
 * Migrate anonymous-mode data to a signed-in Cognito user.
 *
 * MERGE, don't skip: the anonymous habits/check-ins are re-owned by the Cognito
 * user and enqueued as creates. Each create carries a stable `clientId`, so the
 * server dedupes it — a habit the user already has on another device (delivered
 * by the subsequent pull) is NOT duplicated, and the anon data is never lost.
 *
 * Re-migration is naturally idempotent: db.habits/db.checkins are keyed by
 * habitId/id, so re-owning a row overwrites in place. A second cold-boot pass
 * finds no rows still owned by anonUserId → "nothing to migrate".
 */
export async function migrateAnonymousToUser({ anonUserId, newUserId }) {
  const localHabits = await db.habits
    .where('userId')
    .equals(anonUserId)
    .filter((h) => !h.deletedAt)
    .toArray();
  const localCheckins = await db.checkins
    .where('userId')
    .equals(anonUserId)
    .filter((c) => !c.deletedAt)
    .toArray();

  if (localHabits.length === 0 && localCheckins.length === 0) {
    return { migrated: false, reason: 'nothing to migrate' };
  }

  const now = new Date().toISOString();
  await db.transaction('rw', db.habits, db.checkins, db.mutationQueue, async () => {
    for (const h of localHabits) {
      await db.habits.put({
        ...h,
        userId: newUserId,
        syncStatus: 'local',
        localUpdatedAt: now,
      });
    }
    for (const c of localCheckins) {
      await db.checkins.put({
        ...c,
        userId: newUserId,
        syncStatus: 'local',
        localUpdatedAt: now,
      });
    }
  });

  // Enqueue creates. Checkins ride on the habit's create (recordIds reference
  // the local- ID; the queue rewrites them after the habit create lands).
  for (const h of localHabits) {
    const payload = {
      name: h.name,
      icon: h.icon,
      color: h.color,
      schedule: h.schedule,
      targetDays: h.targetDays,
      // Stable idempotency key — same value the anon create used, so if that
      // create is also still queued the server collapses both to one habit.
      clientId: h.habitId,
    };
    await queueRepo.enqueue({
      entity: 'habit',
      op: 'create',
      recordId: h.habitId,
      payload,
    });
  }
  for (const c of localCheckins) {
    await queueRepo.enqueue({
      entity: 'checkin',
      op: 'create',
      recordId: c.id,
      payload: { habitId: c.habitId, date: c.date },
    });
  }

  return { migrated: true, habits: localHabits.length, checkins: localCheckins.length };
}
